/**
 * ブラウザの機能サポート状況を確認するユーティリティ関数
 */

/**
 * Web Speech API (SpeechRecognition) がブラウザで利用可能かどうかを確認する
 * @returns {boolean} SpeechRecognitionが利用可能な場合はtrue、そうでない場合はfalse
 */
export function isSpeechRecognitionAvailable(): boolean {
  try {
    // Web Speech APIのクロスブラウザ対応を確認
    const isAvailable = !!(window.SpeechRecognition || window.webkitSpeechRecognition);
    return isAvailable;
  } catch (error) {
    console.error("Error checking SpeechRecognition availability:", error);
    return false;
  }
}

/**
 * デバイスがiPhoneかどうかを確認する
 * @returns {boolean} iPhoneの場合はtrue、そうでない場合はfalse
 */
export function isiPhoneDevice(): boolean {
  return /iPhone/i.test(navigator.userAgent);
}

/**
 * デバイスがAndroidかどうかを確認する
 * @returns {boolean} Androidデバイスの場合はtrue、そうでない場合はfalse
 */
export function isAndroidDevice(): boolean {
  return /Android/i.test(navigator.userAgent);
}

/**
 * ブラウザがSafariかどうかを確認する
 * @returns {boolean} Safariブラウザの場合はtrue、そうでない場合はfalse
 */
export function isSafariBrowser(): boolean {
  return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
}

/**
 * ブラウザがChromeかどうかを確認する
 * @returns {boolean} Chromeブラウザの場合はtrue、そうでない場合はfalse
 */
export function isChromeBrowser(): boolean {
  return /Chrome/i.test(navigator.userAgent) && !/Edg/i.test(navigator.userAgent);
}

/**
 * ブラウザがFirefoxかどうかを確認する
 * @returns {boolean} Firefoxブラウザの場合はtrue、そうでない場合はfalse
 */
export function isFirefoxBrowser(): boolean {
  return /Firefox/i.test(navigator.userAgent);
}

/**
 * モバイルデバイスのブラウザかどうか確認する
 * @returns {boolean} モバイルブラウザの場合はtrue、そうでない場合はfalse
 */
export function isMobileBrowser(): boolean {
  return /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}

/**
 * サポート対象外のモバイルブラウザかどうかを判定する
 * @returns {boolean} サポート対象外のモバイルブラウザの場合はtrue
 */
export function isUnsupportedMobileBrowser(): boolean {
  // モバイル環境で音声認識が動作しないブラウザを判定
  const isIPhoneSafari = isiPhoneDevice() && isSafariBrowser();
  const isAndroidChrome = isAndroidDevice() && isChromeBrowser();
  const isMobileFirefox = isMobileBrowser() && isFirefoxBrowser();
  return isIPhoneSafari || isAndroidChrome || isMobileFirefox;
}

/**
 * ブラウザの機能サポート状況を確認する
 * @returns {Object} サポート状況のオブジェクト
 */
export function checkBrowserCapabilities() {
  const isRecognitionAvailable = isSpeechRecognitionAvailable();
  const isMobile = isMobileBrowser();
  const isUnsupported = isUnsupportedMobileBrowser();
  const isFirefox = isFirefoxBrowser();

  return {
    speechRecognition: isRecognitionAvailable,
    isMobile,
    isUnsupportedMobile: isUnsupported,
    isFirefox,
    // 将来的に他の機能のチェックを追加できます
  };
}
